import { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { id: "first_conversation", label: "First Conversation", detail: "Free · 30 min" },
  { id: "workshop", label: "Governance Archaeology™ Workshop", detail: "2 hours · from $6,500" },
];

const nextWeekdays = (count) => {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days.push(new Date(d));
  }
  return days;
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookCall() {
  const [service, setService] = useState("first_conversation");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", organisation: "" });
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const days = useMemo(() => nextWeekdays(15), []);

  const loadSlots = useCallback(async (d, svc) => {
    setLoadingSlots(true);
    setSlot(null);
    try {
      const { data } = await axios.get(`${API}/bookings/availability`, { params: { date: d, service: svc } });
      setSlots(data.slots);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (date) loadSlots(date, service);
  }, [date, service, loadSlots]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !date || !slot) {
      toast.error("Please pick a time and fill in your name and email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, service, date, time: slot });
      setConfirmed(data);
      toast.success("Booking confirmed — check your inbox.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
      if (err.response?.status === 409) loadSlots(date, service);
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) {
    const nice = new Date(`${confirmed.date}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
    return (
      <section id="book" data-testid="book-section" className="py-24 md:py-32 border-b border-line">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <CalendarCheck className="mx-auto text-ember mb-6" size={40} />
          <h2 data-testid="booking-confirmed-heading" className="font-serif text-3xl md:text-5xl tracking-tight mb-6">
            You're booked, <em className="text-ember">{confirmed.name.split(" ")[0]}.</em>
          </h2>
          <p className="text-stone2 leading-relaxed mb-4">
            {services.find((s) => s.id === confirmed.service)?.label} — {nice} at {confirmed.time} AEST.
          </p>
          <p className="text-stone2 text-sm">A confirmation with an "Add to Google Calendar" link is on its way to your inbox.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid="book-section" className="py-24 md:py-32 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">Book a time</p>
        <h2 data-testid="book-heading" className="font-serif text-3xl md:text-5xl tracking-tight mb-4 max-w-3xl">
          Pick a time. <em className="text-ember">We'll yarn.</em>
        </h2>
        <p className="text-stone2 max-w-2xl mb-14 leading-relaxed">
          Sessions run Monday to Friday, 9am–5pm AEST — online or on Country.
          The first conversation is always free.
        </p>

        <div className="grid lg:grid-cols-12 gap-px bg-line/40 border border-line/40">
          <div className="lg:col-span-4 bg-ink p-6 md:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mb-4">1 · Session</p>
            <div className="space-y-3">
              {services.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`service-${s.id}`}
                  onClick={() => setService(s.id)}
                  className={`w-full text-left p-4 border transition-colors duration-300 ${
                    service === s.id ? "border-ember bg-surface2" : "border-line hover:border-ember/50"
                  }`}
                >
                  <span className="font-serif text-lg block">{s.label}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ember">{s.detail}</span>
                </button>
              ))}
            </div>

            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-8 mb-4">2 · Day</p>
            <div className="grid grid-cols-5 gap-1.5">
              {days.map((d) => {
                const v = iso(d);
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`day-${v}`}
                    onClick={() => setDate(v)}
                    className={`p-2 border text-center transition-colors duration-300 ${
                      date === v ? "border-ember bg-surface2 text-ember" : "border-line text-bone/80 hover:border-ember/50"
                    }`}
                  >
                    <span className="font-mono text-[9px] uppercase block text-stone2">{d.toLocaleDateString("en-AU", { weekday: "short" })}</span>
                    <span className="font-serif text-base">{d.getDate()}</span>
                    <span className="font-mono text-[9px] uppercase block text-stone2">{d.toLocaleDateString("en-AU", { month: "short" })}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3 bg-surface1 p-6 md:p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mb-4">3 · Time (AEST)</p>
            {!date ? (
              <p className="text-sm text-stone2" data-testid="slots-placeholder">Pick a day first.</p>
            ) : loadingSlots ? (
              <p className="text-sm text-stone2">Checking availability…</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-stone2" data-testid="slots-empty">No times left on this day — try another.</p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5" data-testid="slot-grid">
                {slots.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`slot-${s}`}
                    onClick={() => setSlot(s)}
                    className={`font-mono text-sm py-2.5 border transition-colors duration-300 ${
                      slot === s ? "border-ember bg-ember text-ink" : "border-line text-bone/90 hover:border-ember/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <motion.form
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="booking-form"
            className="lg:col-span-5 bg-ink p-6 md:p-8 flex flex-col"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mb-4">4 · Your details</p>
            <div className="space-y-4 flex-1">
              <input data-testid="booking-name-input" className="field-dark w-full px-4 py-3 text-sm" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input type="email" data-testid="booking-email-input" className="field-dark w-full px-4 py-3 text-sm" placeholder="you@organisation.au *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="booking-org-input" className="field-dark w-full px-4 py-3 text-sm" placeholder="Organisation (optional)" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} />
            </div>
            <div className="mt-6">
              {date && slot && (
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ember mb-4" data-testid="booking-summary">
                  {services.find((s) => s.id === service)?.label} · {date} · {slot} AEST
                </p>
              )}
              <button
                type="submit"
                disabled={busy || !slot}
                data-testid="booking-submit-btn"
                className="w-full font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Booking…" : "Confirm booking"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
